import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { exchangeToken } from '../../api/auth.api';
import { getApiErrorCode, getApiErrorStatus, noAccessPath } from '../../api/client';
import { consumeImpersonationHandoff } from '../../lib/impersonation-handoff';
import { useAuthStore } from '../../stores/auth.store';

/**
 * JT's launch button opens /auth/callback#jt_token=<token>. The fragment is
 * never sent to a server (no access logs, no Referer), so it's checked first;
 * ?jt_token= is the legacy fallback.
 */
function readJtToken(): string | null {
  const hash = window.location.hash.replace(/^#/, '');
  const fromHash = hash ? new URLSearchParams(hash).get('jt_token') : null;
  return fromHash || new URLSearchParams(window.location.search).get('jt_token');
}

export function AuthCallback() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const [error, setError] = useState<string | null>(null);
  // StrictMode runs effects twice in dev. The first run strips the token from
  // the URL, so a second run would wrongly report "No token provided".
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const takeoverKey = params.get('takeover_token');
    const jtToken = readJtToken();

    // Remove credentials from the URL immediately per security spec — both the
    // query string and the fragment — before anything else can read them.
    if (window.location.search || window.location.hash) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Impersonation handoff (takeover or Manage as) — Section 13B.2. The
    // originating tab stashed a token under a random localStorage key and
    // opened this URL in a new tab. We pick it up, clear the storage entry,
    // and set up auth as the impersonated user.
    if (takeoverKey) {
      try {
        const handoff = consumeImpersonationHandoff(takeoverKey);
        if (!handoff) {
          setError('Session handoff expired or already used. Start it again from your original Engage tab.');
          return;
        }
        setAuth(handoff.token, handoff.user, {
          isTakeover: true,
          takeoverBy: handoff.takeoverBy,
          // Payloads written before Manage as existed carry no kind.
          impersonationKind: handoff.kind ?? 'takeover',
          impersonationExpiresAt: handoff.expiresAt ?? null,
          // Never carry a JT token into an impersonation tab.
          jtToken: null,
        });
        navigate('/dashboard', { replace: true });
      } catch {
        setError('Failed to parse takeover handoff.');
      }
      return;
    }

    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (!jtToken) {
      setError('No token provided. Please launch Engage from within JobTetris.');
      return;
    }

    exchangeToken(jtToken)
      .then(({ token, user, is_super_admin }) => {
        // Stash the JT token for session-scoped reuse (manual sync, etc.).
        // See stores/auth.store.ts for the security trade-off rationale.
        setAuth(token, user, { isSuperAdmin: is_super_admin, jtToken });
        navigate('/dashboard', { replace: true });
      })
      .catch((err: unknown) => {
        // Access refusals (ENGAGE_ACCESS_NOT_GRANTED, ENGAGE_NOT_ENABLED_FOR_ORG)
        // get a page that explains what to do; anything else is a plain failure.
        const code = getApiErrorCode(err);
        if (getApiErrorStatus(err) === 403 && code) {
          navigate(noAccessPath(code), { replace: true });
          return;
        }
        setError('Authentication failed. Please try launching Engage again from JobTetris.');
      });
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="text-gray-500">Signing you in...</p>
      </div>
    </div>
  );
}
